import {
  DY_CURRENCY,
  DY_KEYWORD_SEARCH,
  DY_ADD_TO_CART,
  DY_REMOVE_FROM_CART,
  DY_SORT_ITEMS,
  DY_FILTER_ITEMS,
  DY_ADD_TO_WISHLIST,
  DY_SYNC_CART,
  dyEventByName,
} from './constants';
import { isNumberOrString, isNumbers, isStrings } from './utils';
import type { DyCartSyncItem, DyDataEvent, DyDateEventCreator } from './types';

/**
 * @name createDyKeywordSearch
 * @description Возвращает объект с данными для вызова события DY - DY:keyword_search (событие вендора - keyword-search-v1).
 *  Вернёт false если переданные данные невалидны.
 * @param keywords
 */
export const createDyKeywordSearch = (keywords: string) => {
  if (DY_KEYWORD_SEARCH in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_KEYWORD_SEARCH];
    return {
      name,
      properties: {
        eventType,
        keywords,
      },
    };
  }
  return false;
};

/**
 * @name createDYCartItem
 * @description Возвращает элемент корзины DY или false если переданные данные невалидны.
 * @param productId
 * @param quantity
 * @param value
 * @returns {false|{quantity: number, productId: number | string, currency: string, value: number}}
 */
export const createDYCartItem = (productId: unknown, quantity: unknown, value: unknown) => {
  // Валидация данных
  if (!isNumberOrString(productId) || !isNumbers(quantity, value)) {
    return false;
  }

  // Создание данных
  return {
    productId,
    quantity,
    value: Number(quantity) * Number(value),
    currency: DY_CURRENCY,
  };
};

/**
 *
 * @name createDyAddCartEvent
 * @description Возвращает объект с данными для вызова события DY - DY:add_to_cart (событие вендора - add-to-cart-v1).
 *  Вернёт false если переданные данные невалидны.
 * @param productId
 * @param quantity
 * @param value
 */
export const createDyAddCartEvent: DyDateEventCreator = (productId, quantity, value) => {
  const data = createDYCartItem(productId, quantity, value);

  if (data && DY_ADD_TO_CART in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_ADD_TO_CART];
    return {
      name,
      properties: {
        eventType,
        ...data,
      },
    };
  }
  return false;
};

/**
 * @name createDyRemoveCartEvent
 * @description Возвращает объект с данными для вызова события DY - DY:remove_from_cart (событие вендора - remove-from-cart-v1).
 *  Вернёт false если переданные данные невалидны.
 * @param productId
 * @param quantity
 * @param value
 */
export const createDyRemoveCartEvent: DyDateEventCreator = (productId, quantity, value) => {
  const data = createDYCartItem(productId, quantity, value);

  if (data && DY_REMOVE_FROM_CART in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_REMOVE_FROM_CART];
    return {
      name,
      properties: {
        eventType,
        ...data,
      },
    };
  }
  return false;
};

/**
 * @name createDySortItemsEvent
 * @description Возвращает объект с данными для вызова события DY - DY:sort_items (событие вендора - sort-items-v1).
 *  Вернёт false если переданные данные невалидны.
 * @param sortBy
 * @param sortOrder
 */
export const createDySortItemsEvent: DyDateEventCreator = (sortBy, sortOrder) => {
  if (isStrings(sortBy, sortOrder) && DY_SORT_ITEMS in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_SORT_ITEMS];
    return {
      name,
      properties: {
        eventType,
        sortBy,
        sortOrder,
      },
    };
  }
  return false;
};

/**
 * @name createDyFilterEvent
 * @description Возвращает объект с данными для вызова события DY - DY:filter_items (событие вендора - filter-items-v1).
 * @param filterType
 * @param filterNumericValue
 */
export const createDyFilterEvent: DyDateEventCreator = (filterType, filterNumericValue) => {
  if (isNumberOrString(filterNumericValue) && isStrings(filterType) && DY_FILTER_ITEMS in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_FILTER_ITEMS];
    return {
      name,
      properties: {
        eventType,
        filterType,
        ...(typeof filterNumericValue === 'number'
          ? { filterNumericValue }
          : { filterStringValue: filterNumericValue }),
      },
    };
  }
  return false;
};

/**
 * @name createDyAddToWishlistEvent
 * @description Возвращает объект с данными для вызова события DY - DY:add_to_wishlist (событие вендора - add-to-wishlist-v1).
 *  Вернёт false если переданные данные невалидны.
 * @param productId
 */
export const createDyAddToWishlistEvent: DyDateEventCreator = productId => {
  if (isNumberOrString(productId) && DY_ADD_TO_WISHLIST in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_ADD_TO_WISHLIST];
    return {
      name,
      properties: {
        eventType,
        productId,
      },
    };
  }
  return false;
};

export const createDyCartSyncEvent = (products: DyCartSyncItem[]): DyDataEvent | false => {
  if (DY_SYNC_CART in dyEventByName) {
    const { eventType, name } = dyEventByName[DY_SYNC_CART];
    return {
      name,
      properties: {
        eventType,
        currency: DY_CURRENCY,
        cart: products,
      },
    };
  }
  return false;
};
